// NPM packages
import React from 'react';
import { observer } from 'mobx-react-lite';
import * as yup from 'yup';

// All other imports
import TextField from 'components/core/TextField';
import { useServices } from 'services';
import Form from 'components/core/Form';
import AffiliationAutocomplete from 'components/core/AffiliationAutocomplete';
import UpdateProfileData from 'types/UpdateProfileData';

export interface UpdateProfileProps {
  className?: string;
}

interface ErrorComponentProps {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  rejectedVal: any;
}

const ORCID_ID_REGEX = /^(\d{4}-){3}\d{3}[\dX]$/;
const ORCID_FORMAT_MSG =
  "ORCID iD must be in the format XXXX-XXXX-XXXX-XXXX (e.g. 0000-1234-5678-9101).";

const getSchema = (isT3: boolean): yup.ObjectSchema<UpdateProfileData> =>
  yup.object({
    email: yup
      .string()
      .required()
      .email()
      .label('Email'),
    firstName: yup.string().required().label('First Name'),
    lastName: yup.string().required().label('Last Name'),
    affiliation: yup.string().required().label('Affiliation'),
    orcidId: yup
      .string()
      .trim()
      .when([], {
        is: () => isT3,
        then: (schema) =>
          schema
            .required(ORCID_FORMAT_MSG)
            .min(1, ORCID_FORMAT_MSG)
            .matches(
              ORCID_ID_REGEX,
              ORCID_FORMAT_MSG
            ),
        otherwise: (schema) =>
          schema.matches(ORCID_ID_REGEX, {
            message: ORCID_FORMAT_MSG,
            excludeEmptyString: true,
          }),
      })
      .label('ORCID iD'),
  });

function ErrorComponent(props: ErrorComponentProps): React.ReactElement {
  const { rejectedVal } = props;
  return (
    <div>
      <div>Profile failed to update: {rejectedVal.message}</div>
      <div style={{ marginTop: '8px' }}>{rejectedVal.stack}</div>
    </div>
  );
}

function UpdateProfile(props: UpdateProfileProps): React.ReactElement | null {
  const { className } = props;
  const { authService } = useServices();
  const user = authService.user;
  const isDemoUser = user?.isDemoUser ?? false;
  const isT3 = user?.membershipTier === 'Tier 3';
  const schema = getSchema(isT3);
  const defaultValues = {
    email: user?.email || '',
    firstName: user?.firstName || '',
    lastName: user?.lastName || '',
    affiliation: user?.affiliation || '',
    orcidId: user?.orcidId || '',
  };

  return (
    <Form<UpdateProfileData>
      className={className}
      schema={schema}
      defaultValues={defaultValues}
      onSubmit={(data) => authService.updateProfile(data)}
      replaceFormWithSuccess={false}
      getSuccessMessage={() => <div>Profile successfully updated!</div>}
      getErrorMessage={(rejectedVal) => (
        <ErrorComponent rejectedVal={rejectedVal} />
      )}
      submitText="Update"
      submitButtonProps={{ disabled: isDemoUser }}
    >
      {(formState) => {
        return (
          <>
            <TextField
              formState={formState}
              fieldName="email"
              textFieldProps={{
                type: 'email',
                disabled: isDemoUser,
                onBlur: (e: React.FocusEvent<HTMLInputElement>) => {
                  const lowercaseValue = e.target.value.toLowerCase();
                  formState.setValue('email', lowercaseValue);
                },
              }}
            />
            <TextField formState={formState} fieldName="firstName" textFieldProps={{ disabled: isDemoUser }} />
            <TextField formState={formState} fieldName="lastName" textFieldProps={{ disabled: isDemoUser }} />
            <AffiliationAutocomplete
              formState={formState}
              fieldName="affiliation"
              textFieldProps={{ disabled: isDemoUser }}
            />
            <TextField
              formState={formState}
              fieldName="orcidId"
              textFieldProps={{
                label: isT3 ? 'ORCID iD' : 'ORCID iD (optional)',
                placeholder: '0000-0002-1825-0097',
                helperText: isT3
                  ? 'Required while your account is in Tier 3.'
                  : undefined,
                required: isT3,
                InputLabelProps: { sx: { whiteSpace: 'nowrap' } },
              }}
            />
          </>
        );
      }}
    </Form>
  );
}

export default observer(UpdateProfile);
